import React from 'react';
import { Group, Line, Circle } from 'react-konva';
import type { KonvaEventObject } from 'konva/lib/Node';

export type InProgressPolygonOverlayProps = {
  points: [number, number][];
  color: string;
  scale?: number;
  closed?: boolean;
  visible?: boolean;
  /** Mark vertex 0 distinctly (a black inner ring) — used for four-point
   *  types where the clockwise drawing start point matters. */
  markStart?: boolean;
  /** When provided, individual points are draggable and this callback fires with new image-pixel coords. */
  onPointDrag?: (idx: number, x: number, y: number) => void;
};

const InProgressPolygonOverlay: React.FC<InProgressPolygonOverlayProps> = ({
  points,
  color,
  scale = 1,
  closed = false,
  visible = true,
  markStart = false,
  onPointDrag,
}) => {
  if (!visible || points.length === 0) return null;

  const handleDragMove = (idx: number) => (e: KonvaEventObject<DragEvent>) => {
    onPointDrag?.(idx, e.target.x(), e.target.y());
  };

  const radius = 8 / scale;
  const stroke = 1.5 / scale;
  const hit = 12 / scale;
  const lineWidth = 3 / scale;

  return (
    <Group>
      {points.length > 1 && (
        <Line
          points={points.flat()}
          closed={closed && points.length > 2}
          stroke={color}
          strokeWidth={lineWidth}
          fill={closed && points.length > 2 ? `${color}20` : undefined}
        />
      )}
      {points.map(([x, y], idx) => (
        <React.Fragment key={idx}>
          <Circle
            x={x}
            y={y}
            radius={radius}
            fill={color}
            stroke="white"
            strokeWidth={stroke}
            hitStrokeWidth={hit}
            draggable={!!onPointDrag}
            onDragMove={onPointDrag ? handleDragMove(idx) : undefined}
            onClick={onPointDrag ? (e) => { e.cancelBubble = true; } : undefined}
            onMouseDown={onPointDrag ? (e) => { e.cancelBubble = true; } : undefined}
            onMouseEnter={onPointDrag ? (e) => { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'grab'; } : undefined}
            onMouseLeave={onPointDrag ? (e) => { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'default'; } : undefined}
          />
          {/* Start-vertex marker: subtle black inner ring inside the white
              circle, so the clockwise drawing start point is distinguishable. */}
          {markStart && idx === 0 && (
            <Circle
              x={x}
              y={y}
              radius={radius * 0.45}
              stroke="black"
              strokeWidth={stroke}
              listening={false}
            />
          )}
        </React.Fragment>
      ))}
    </Group>
  );
};

export default InProgressPolygonOverlay;
